import { useState, useEffect } from 'react'
import { adaptVacanciesFromBackend } from '@/utils/adapters/vacancyAdapter'
import { SUPABASE_FUNCTIONS_URL } from '@/utils/apiEndpoints'

/**
 * Custom hook for loading and polling vacancies with event listener support
 *
 * @param {Object} options - Configuration options
 * @param {number} options.limit - Maximum number of vacancies to load (default: 8)
 * @param {string} options.status - Filter by vacancy status (default: 'gepubliceerd')
 * @param {number} options.pollingInterval - Polling interval in milliseconds (default: 30000)
 * @param {boolean} options.enablePolling - Whether to enable polling (default: true)
 * @returns {Object} { vacancies, isLoading, error }
 */
export const useVacancyLoading = ({
  limit = 8,
  status = 'gepubliceerd',
  pollingInterval = 30000,
  enablePolling = true
} = {}) => {
  const [vacancies, setVacancies] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const loadVacancies = async () => {
      try {
        console.log('Loading vacancies from server...')

        let allVacancies = []

        // Try to load from server first (limit for homepage display)
        try {
          const response = await fetch(`${SUPABASE_FUNCTIONS_URL}/public-vacancies?limit=${limit}`)

          if (response.ok) {
            const result = await response.json()
            const serverVacancies = adaptVacanciesFromBackend(result.data || [])
            console.log('Server vacancies loaded:', serverVacancies.length)
            allVacancies = [...serverVacancies]
          }
        } catch (serverError) {
          console.log('Server not available, skipping server vacancies')
        }

        // Sort by creation date (newest first)
        allVacancies.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))

        console.log('Total vacancies loaded:', allVacancies.length)
        setVacancies(allVacancies)
        setError(null)
        setIsLoading(false)
      } catch (err) {
        console.error('❌ Error loading vacancies:', err)
        setError(err.message)
        setVacancies([])
        setIsLoading(false)
      }
    }

    // Initial load
    loadVacancies()

    // Set up polling for updates (with visibility check)
    let interval = null
    if (enablePolling) {
      interval = setInterval(() => {
        // Only poll if page is visible
        if (!document.hidden) {
          loadVacancies()
        }
      }, pollingInterval)
    }

    // Event listeners for various update triggers
    const handleVacancyCreated = (event) => {
      console.log('📢 VacancyCreated event received!', event.detail)
      const newVacancy = event.detail?.vacancyData

      if (!newVacancy) {
        console.error('No vacancy data in event')
        return
      }

      console.log('Processing new vacancy:', newVacancy.titel, 'ID:', newVacancy.id)

      // Add the new vacancy to the beginning of the list (most recent first)
      setVacancies(prev => {
        // Check if vacancy already exists to avoid duplicates
        const exists = prev.some(v => v.id === newVacancy.id)
        if (exists) {
          console.log('Vacancy already exists, skipping duplicate')
          return prev
        }
        console.log('✅ Adding new vacancy to list:', newVacancy.titel)
        return [newVacancy, ...prev]
      })
    }

    // Listen for localStorage changes as fallback
    const handleStorageChange = (e) => {
      if (e.key === 'vacancies') {
        console.log('Vacancies updated in localStorage, reloading...', e)
        // Small delay to ensure localStorage is fully updated
        setTimeout(() => {
          loadVacancies()
        }, 50)
      }
    }

    // Listen for when user returns to the page to ensure fresh data
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('Page became visible, refreshing vacancies...')
        loadVacancies()
      }
    }

    // Register event listeners
    window.addEventListener('vacancyCreated', handleVacancyCreated)
    window.addEventListener('storage', handleStorageChange)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    // Cleanup function
    return () => {
      if (interval) clearInterval(interval)
      window.removeEventListener('vacancyCreated', handleVacancyCreated)
      window.removeEventListener('storage', handleStorageChange)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [limit, status, pollingInterval, enablePolling])

  return { vacancies, isLoading, error }
}
